<template>
  <div class="configuracion-departamentos-container p-4">
    <div class="mb-3">
      <vs-button icon="arrow_back" size="small" type="border" @click="$router.push('/actividades-control')">Volver</vs-button>
    </div>

    <!-- VISTA 1: ADMINISTRADOR / ROOT -->
    <vx-card v-if="userRoot || esAdministrador" title="Estructura & Configuración Global (Root / Admin)">
      <p class="mb-4 text-gray-600">
        Administración central de departamentos, asignación de Jefes de Área y configuración general.
      </p>

      <vs-tabsAlignment alignment="left">
        <vs-tabs>

          <!-- TAB 1: DEPARTAMENTOS & JEFES -->
          <vs-tab label="Departamentos" icon="account_tree">
            <div class="py-4">
              <!-- Toolbar Tab 1 -->
              <div class="flex justify-end mb-3">
                <vx-tooltip text="Recargar información de departamentos">
                  <vs-button
                    size="small"
                    type="border"
                    color="primary"
                    icon="refresh"
                    :class="{ 'spin-icon': recargandoDeptos }"
                    @click="recargarDepartamentos"
                  >Recargar</vs-button>
                </vx-tooltip>
              </div>
              <vs-table :data="departamentos" stripe>
                <template slot="thead">
                  <vs-th>Departamento</vs-th>
                  <vs-th>Iniciales</vs-th>
                  <vs-th>Jefe / Responsable</vs-th>
                  <vs-th>Categorías</vs-th>
                  <vs-th>Fines de Semana</vs-th>
                  <vs-th>Acciones</vs-th>
                </template>
                <template slot-scope="{ data }">
                  <vs-tr v-for="(depto, index) in data" :key="index">
                    <vs-td>
                      <vs-chip :color="getColorDepartamento(depto.tdp_id)">{{ depto.tdp_nombre }}</vs-chip>
                    </vs-td>
                    <vs-td><span class="font-bold">{{ depto.tdp_iniciales }}</span></vs-td>
                    <vs-td>
                      <div v-if="depto.responsable">
                        <p class="font-semibold text-primary mb-0">{{ depto.responsable.nombres }} {{ depto.responsable.apellidos }}</p>
                        <p class="text-xs text-gray-500 mb-0">{{ depto.responsable.email }}</p>
                      </div>
                      <span v-else class="text-gray-400 italic">Sin asignar</span>
                    </vs-td>
                    <vs-td>
                      <vs-chip color="primary">{{ depto.categorias ? depto.categorias.length : 0 }} categorías</vs-chip>
                    </vs-td>
                    <vs-td>
                      <vx-tooltip text="Permitir registro de tareas en fines de semana">
                        <vs-switch :value="depto.tdp_incluye_fines_semana" @input="toggleFinesSemana(depto)" color="success" :disabled="guardandoDeptoId !== null" />
                      </vx-tooltip>
                    </vs-td>
                    <vs-td>
                      <div class="flex gap-2">
                        <vx-tooltip text="Asignar Jefe / Responsable de área">
                          <vs-button size="small" icon="person" @click="asignarResponsable(depto)" color="primary" type="flat" />
                        </vx-tooltip>
                        <vx-tooltip text="Gestionar equipo de este departamento">
                          <vs-button size="small" icon="groups" @click="gestionarEquipo(depto)" color="success" type="flat" />
                        </vx-tooltip>
                        <vx-tooltip text="Ver y gestionar categorías del departamento">
                          <vs-button size="small" icon="category" @click="verCategorias(depto)" color="warning" type="flat" />
                        </vx-tooltip>
                      </div>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </div>
          </vs-tab>

          <!-- TAB 2: CONTROL DE JEFES DE ÁREA -->
          <vs-tab label="Responsables / Jefes de Área" icon="supervisor_account">
            <div class="py-4">
              <!-- Buscador + contador + recarga -->
              <div class="flex items-center gap-3 mb-4">
                <vs-input
                  v-model="busquedaJefes"
                  placeholder="Buscar colaborador de nómina..."
                  icon="search"
                  class="flex-1"
                />
                <vx-tooltip text="Recargar lista de empleados">
                  <vs-button
                    size="small"
                    type="border"
                    color="primary"
                    icon="refresh"
                    :class="{ 'spin-icon': cargandoJefes }"
                    @click="recargarJefes"
                  />
                </vx-tooltip>
                <vx-tooltip text="Total de colaboradores habilitados como Jefe de Área">
                  <vs-chip color="warning">
                    <i class="feather icon-star mr-1"></i>
                    {{ empleadosJefes.filter(e => e.jefe_departamento == '1' || e.jefe_departamento == 1).length }} Jefes activos
                  </vs-chip>
                </vx-tooltip>
              </div>

              <!-- Loading -->
              <div v-if="cargandoJefes" class="text-center py-10 text-gray-400">
                <i class="feather icon-loader animate-spin text-2xl block mb-2"></i>
                <span class="text-sm">Cargando empleados de nómina...</span>
              </div>

              <!-- Lista empleados con switch -->
              <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div
                  v-for="emp in empleadosJefesFiltrados"
                  :key="emp.idusuario"
                  class="jefe-card"
                  :class="{ 'jefe-card--activo': emp.jefe_departamento == '1' || emp.jefe_departamento == 1 }"
                >
                  <div class="jefe-avatar" :class="{ 'jefe-avatar--activo': emp.jefe_departamento == '1' || emp.jefe_departamento == 1 }">
                    {{ iniciales(emp.nombre_completo) }}
                  </div>
                  <div class="jefe-info">
                    <span class="jefe-nombre">{{ emp.nombre_completo }}</span>
                    <span class="jefe-meta">
                      <i class="feather icon-briefcase mr-1"></i>{{ emp.departamento_nombre }}
                    </span>
                  </div>
                  <div class="jefe-actions">
                    <vx-tooltip :text="(emp.jefe_departamento == '1' || emp.jefe_departamento == 1) ? 'Revocar jefatura' : 'Habilitar como Jefe de Área'">
                      <vs-switch
                        :value="emp.jefe_departamento == '1' || emp.jefe_departamento == 1"
                        @input="val => cambiarJefatura(emp, val)"
                        :disabled="guardandoJefe === emp.idusuario"
                        color="primary"
                      />
                    </vx-tooltip>
                  </div>
                </div>

                <div v-if="!empleadosJefesFiltrados.length" class="col-span-3 text-center py-6 text-gray-400 text-sm">
                  <i class="feather icon-users text-2xl block mb-2"></i>
                  No hay empleados que coincidan con la búsqueda.
                </div>
              </div>
            </div>
          </vs-tab>

        </vs-tabs>
      </vs-tabsAlignment>
    </vx-card>

    <!-- SECCIÓN PARA ROOT: GESTIÓN DE EQUIPO DE UN DEPARTAMENTO SELECCIONADO -->
    <vx-card
      ref="cardGestionEquipo"
      v-if="(userRoot || esAdministrador) && deptoSeleccionadoRoot"
      :title="`Gestión de Equipo — ${deptoSeleccionadoRoot.tdp_nombre}`"
      class="mt-6 border-2 border-primary"
    >
      <div class="flex justify-between items-center mb-4">
        <p class="text-gray-600 m-0">Administrando colaboradores asignados a <strong>{{ deptoSeleccionadoRoot.tdp_nombre }}</strong> (Modo Root/Admin)</p>
        <vs-button size="small" color="danger" type="flat" icon="close" @click="deptoSeleccionadoRoot = null">Cerrar vista Root</vs-button>
      </div>
      <GestionEquipoDepartamento :departamento="deptoSeleccionadoRoot" />
    </vx-card>

    <!-- VISTA 2: JEFE DE DEPARTAMENTO -->
    <vx-card
      v-if="miDepartamentoComoJefe"
      :title="`Gestión de Área — ${miDepartamentoComoJefe.tdp_nombre}`"
      class="mt-6"
    >
      <p class="mb-4 text-gray-600">
        Administra la distribución del equipo de tu departamento y configura las categorías de actividades de tu área.
      </p>

      <vs-tabsAlignment alignment="left">
        <vs-tabs>
          <vs-tab label="Distribución de Mi Equipo" icon="groups">
            <div class="py-4">
              <GestionEquipoDepartamento :departamento="miDepartamentoComoJefe" />
            </div>
          </vs-tab>

          <vs-tab label="Categorías de Actividad" icon="category">
            <div class="py-4">
              <div class="flex justify-between items-center mb-4">
                <h5 class="font-bold text-gray-800">Categorías de {{ miDepartamentoComoJefe.tdp_nombre }}</h5>
                <vs-button size="small" @click="verCategorias(miDepartamentoComoJefe)" color="primary" icon="settings">
                  Gestionar Categorías
                </vs-button>
              </div>
              <div v-if="miDepartamentoComoJefe.categorias && miDepartamentoComoJefe.categorias.length" class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div
                  v-for="cat in miDepartamentoComoJefe.categorias"
                  :key="cat.id_categoria"
                  class="p-3 border rounded-lg flex items-center gap-3 bg-slate-50"
                >
                  <div class="w-4 h-4 rounded-full flex-shrink-0" :style="{ backgroundColor: cat.color || '#3b82f6' }"></div>
                  <div>
                    <h6 class="font-bold text-sm text-gray-800 m-0">{{ cat.nombre_categoria }}</h6>
                    <p class="text-xs text-gray-500 m-0">{{ cat.descripcion || 'Sin descripción' }}</p>
                  </div>
                </div>
              </div>
              <div v-else class="text-gray-400 text-sm py-4 italic">
                No hay categorías creadas aún. Haz clic en "Gestionar Categorías" para crear la primera.
              </div>
            </div>
          </vs-tab>
        </vs-tabs>
      </vs-tabsAlignment>
    </vx-card>

    <!-- MENSAJE DE ADVERTENCIA SI NO TIENE DEPARTAMENTO COINCIDENTE -->
    <vx-card v-if="!userRoot && !esAdministrador && !miDepartamentoComoJefe" class="mt-6">
      <div class="text-center py-8">
        <i class="feather icon-alert-triangle text-warning text-4xl block mb-3"></i>
        <h4 class="font-bold text-gray-800 mb-2">Sin Departamento Asignado</h4>
        <p class="text-gray-600 max-w-md mx-auto">
          Tu perfil tiene rol de Jefe de Área, pero aún no has sido vinculado como responsable a un departamento específico. Contacta al Administrador Root para que te vincule a tu departamento.
        </p>
      </div>
    </vx-card>

    <!-- Modal asignar responsable -->
    <vs-popup title="Asignar Responsable del Departamento" :active.sync="modalResponsable">
      <div v-if="departamentoSeleccionado" class="p-4">
        <h4 class="font-bold mb-4 text-gray-800">{{ departamentoSeleccionado.tdp_nombre }}</h4>

        <div class="mb-4">
          <label class="text-sm font-semibold mb-1 block">Seleccionar Responsable / Jefe de Área</label>

          <div v-if="cargandoEmpleados" class="flex items-center gap-2 py-3 text-gray-500 text-sm">
            <i class="feather icon-loader animate-spin"></i>
            <span>Cargando empleados de nómina...</span>
          </div>

          <v-select
            v-else
            v-model="responsableSeleccionado"
            :options="empleadosDisponibles"
            :reduce="emp => emp.idusuario"
            label="nombre_completo"
            placeholder="Buscar empleado por nombre..."
            class="w-full"
            :filter-by="filtrarEmpleado"
          >
            <template #option="option">
              <div class="flex flex-col py-1">
                <div class="flex items-center justify-between">
                  <span class="font-semibold text-gray-800">{{ option.nombre_completo }}</span>
                  <span
                    v-if="option.jefe_departamento == '1' || option.jefe_departamento == 1"
                    class="text-xs bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded ml-2"
                  >Jefe de Área</span>
                </div>
                <span class="text-xs text-gray-500">
                  <i class="feather icon-briefcase mr-1"></i>{{ option.departamento_nombre }}
                  <span v-if="option.cedula" class="ml-2"><i class="feather icon-credit-card mr-1"></i>{{ option.cedula }}</span>
                </span>
              </div>
            </template>
            <template #selected-option="option">
              <span>{{ option.nombre_completo }}</span>
            </template>
            <template #no-options>
              <span class="text-gray-400 text-sm">No se encontraron empleados habilitados</span>
            </template>
          </v-select>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <vs-button @click="modalResponsable = false" color="danger" type="flat" :disabled="guardandoResponsable">Cancelar</vs-button>
          <vs-button @click="guardarResponsable" color="primary" :disabled="guardandoResponsable">
            <i v-if="guardandoResponsable" class="feather icon-loader animate-spin mr-1"></i>
            {{ guardandoResponsable ? 'Guardando...' : 'Guardar Responsable' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Modal categorías -->
    <vs-popup title="Gestionar Categorías del Departamento" :active.sync="modalCategorias" fullscreen>
      <div v-if="departamentoSeleccionado" class="p-4">
        <div class="flex justify-between items-center mb-6">
          <h4 class="font-bold text-lg text-gray-800">Categorías de {{ departamentoSeleccionado.tdp_nombre }}</h4>
          <vs-button @click="abrirNuevaCategoria" color="primary" icon="add" :disabled="cargandoCategorias">Nueva Categoría</vs-button>
        </div>

        <div v-if="cargandoCategorias" class="flex items-center gap-2 py-10 justify-center text-gray-500">
          <i class="feather icon-loader animate-spin text-2xl"></i>
          <span class="text-sm">Cargando categorías...</span>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="cat in categoriasDepartamento"
            :key="cat.id_categoria"
            class="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-center gap-3 flex-1">
                <div class="w-8 h-8 rounded-lg flex-shrink-0" :style="{ backgroundColor: cat.color || '#3b82f6' }"></div>
                <div>
                  <h5 class="font-bold text-gray-800 m-0">{{ cat.nombre_categoria }}</h5>
                  <p class="text-sm text-gray-500 m-0">{{ cat.descripcion || 'Sin descripción' }}</p>
                </div>
              </div>
              <div class="flex gap-2">
                <vs-button size="small" icon="edit" @click="editarCategoria(cat)" color="warning" type="flat" :disabled="guardandoCategoria" />
                <vs-button size="small" icon="delete" @click="eliminarCategoria(cat.id_categoria)" color="danger" type="flat" :disabled="guardandoCategoria" />
              </div>
            </div>
          </div>
        </div>

        <div v-if="mostrarFormCategoria" class="mt-6 border-t pt-6">
          <h5 class="font-bold mb-4 text-gray-800">{{ categoriaForm.id_categoria ? 'Editar' : 'Nueva' }} Categoría</h5>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-semibold">Nombre *</label>
              <vs-input v-model="categoriaForm.nombre_categoria" class="w-full" placeholder="Ej: Desarrollo Backend" />
            </div>
            <div>
              <label class="text-sm font-semibold">Color</label>
              <input v-model="categoriaForm.color" type="color" class="w-full h-10 border rounded cursor-pointer" />
            </div>
            <div class="md:col-span-2">
              <label class="text-sm font-semibold">Descripción</label>
              <vs-textarea v-model="categoriaForm.descripcion" class="w-full" rows="2" placeholder="Describe el tipo de actividades..." />
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <vs-button @click="cancelarFormCategoria" color="danger" type="flat" :disabled="guardandoCategoria">Cancelar</vs-button>
            <vs-button @click="guardarCategoria" color="primary" :disabled="guardandoCategoria">
              <i v-if="guardandoCategoria" class="feather icon-loader animate-spin mr-1"></i>
              {{ categoriaForm.id_categoria ? 'Actualizar' : 'Crear' }}
            </vs-button>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { mapState } from 'vuex'
import GestionEquipoDepartamento from './GestionEquipoDepartamento.vue'

export default {
  name: 'ConfiguracionDepartamentos',
  components: { vSelect, GestionEquipoDepartamento },
  data() {
    return {
      departamentos: [],
      recargandoDeptos: false,
      deptoSeleccionadoRoot: null,
      // Modal responsable
      empleadosDisponibles: [],
      cargandoEmpleados: false,
      guardandoResponsable: false,
      departamentoSeleccionado: null,
      responsableSeleccionado: null,
      modalResponsable: false,
      // Modal categorías
      modalCategorias: false,
      cargandoCategorias: false,
      guardandoCategoria: false,
      categoriasDepartamento: [],
      mostrarFormCategoria: false,
      categoriaForm: { id_categoria: null, nombre_categoria: '', descripcion: '', color: '#3b82f6' },
      // Tab control de jefes
      empleadosJefes: [],
      cargandoJefes: false,
      busquedaJefes: '',
      guardandoJefe: null,
      // Depto en proceso de cambio de fin de semana
      guardandoDeptoId: null,
    }
  },
  computed: {
    ...mapState(['auth', 'userRoot', 'susuario']),
    esAdministrador() {
      if (this.userRoot) return true
      const u = JSON.parse(localStorage.getItem('usuario') || '{}')
      return u.id_group == 1 || u.id_group == '1'
    },
    miDepartamentoComoJefe() {
      const usuario = JSON.parse(localStorage.getItem('usuario') || '{}')
      const idusuario = usuario.idusuario
      const userTdpId = usuario.tdp_id
      if (!idusuario) return null

      // 1. Coincidencia directa por tdp_responsable_id
      let depto = this.departamentos.find(
        d => d.tdp_responsable_id && Number(d.tdp_responsable_id) === Number(idusuario)
      )
      if (depto) return depto

      // 2. Coincidencia por tdp_id si es jefe de departamento (jefe_departamento == 1)
      if ((usuario.jefe_departamento == '1' || usuario.jefe_departamento == 1) && userTdpId) {
        depto = this.departamentos.find(d => Number(d.tdp_id) === Number(userTdpId))
        if (depto) return depto
      }

      // 3. Fallback: buscar por tdp_id asignado en usuario
      if (userTdpId) {
        return this.departamentos.find(d => Number(d.tdp_id) === Number(userTdpId)) || null
      }

      return null
    },
    empleadosJefesFiltrados() {
      const q = this.busquedaJefes.toLowerCase()
      if (!q) return this.empleadosJefes
      return this.empleadosJefes.filter(e =>
        (e.nombre_completo || '').toLowerCase().includes(q) ||
        (e.cedula || '').includes(q) ||
        (e.departamento_nombre || '').toLowerCase().includes(q)
      )
    }
  },
  mounted() {
    this.cargarDepartamentos()
    if (this.userRoot || this.esAdministrador) {
      this.cargarEmpleadosJefes()
    }
  },
  methods: {
    // ─── Departamentos ───────────────────────────────────────────────
    async cargarDepartamentos() {
      this.$vs.loading()
      try {
        const res = await this.$http.get(this.$server_url + 'actividades-control/departamentos')
        this.departamentos = res.data || []
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudieron cargar los departamentos', color: 'danger' })
      } finally {
        this.$vs.loading.close()
      }
    },
    async recargarDepartamentos() {
      this.recargandoDeptos = true
      try {
        const res = await this.$http.get(this.$server_url + 'actividades-control/departamentos')
        this.departamentos = res.data || []
        this.$vs.notify({ title: '✅ Actualizado', text: 'Departamentos recargados', color: 'success', iconPack: 'feather', icon: 'icon-refresh-cw', time: 2000 })
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo recargar', color: 'danger' })
      } finally {
        this.recargandoDeptos = false
      }
    },
    async recargarJefes() {
      this.empleadosJefes = []
      await this.cargarEmpleadosJefes()
      this.$vs.notify({ title: '✅ Actualizado', text: 'Lista de responsables recargada', color: 'success', iconPack: 'feather', icon: 'icon-refresh-cw', time: 2000 })
    },
    gestionarEquipo(departamento) {
      this.deptoSeleccionadoRoot = departamento
      this.$nextTick(() => {
        if (this.$refs.cardGestionEquipo && this.$refs.cardGestionEquipo.$el) {
          this.$refs.cardGestionEquipo.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    async toggleFinesSemana(departamento) {
      if (this.guardandoDeptoId !== null) return
      this.guardandoDeptoId = departamento.tdp_id
      try {
        await this.$http.put(this.$server_url + `actividades-control/departamentos/${departamento.tdp_id}`, {
          tdp_incluye_fines_semana: !departamento.tdp_incluye_fines_semana
        })
        departamento.tdp_incluye_fines_semana = !departamento.tdp_incluye_fines_semana
        this.$vs.notify({ title: 'Éxito', text: 'Configuración actualizada', color: 'success' })
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo actualizar', color: 'danger' })
      } finally {
        this.guardandoDeptoId = null
      }
    },

    // ─── Asignar responsable (modal) ─────────────────────────
    async asignarResponsable(departamento) {
      this.departamentoSeleccionado = departamento
      this.responsableSeleccionado = departamento.tdp_responsable_id
      this.empleadosDisponibles = []
      this.modalResponsable = true
      await this.cargarEmpleados()
    },
    async cargarEmpleados() {
      this.cargandoEmpleados = true
      try {
        const res = await this.$http.get(this.$server_url + 'actividades-control/empleados/activos')
        // Solo se pueden asignar como responsable quienes son Jefe de Área habilitados
        this.empleadosDisponibles = (res.data || []).filter(
          e => e.jefe_departamento == '1' || e.jefe_departamento == 1
        )
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo cargar la lista de empleados', color: 'danger' })
      } finally {
        this.cargandoEmpleados = false
      }
    },
    filtrarEmpleado(option, label, search) {
      const s = search.toLowerCase()
      return (
        (option.nombre_completo || '').toLowerCase().includes(s) ||
        (option.cedula || '').includes(s) ||
        (option.email || '').toLowerCase().includes(s) ||
        (option.departamento_nombre || '').toLowerCase().includes(s)
      )
    },
    async guardarResponsable() {
      if (!this.departamentoSeleccionado || this.guardandoResponsable) return
      this.guardandoResponsable = true
      try {
        await this.$http.put(this.$server_url + `actividades-control/departamentos/${this.departamentoSeleccionado.tdp_id}`, {
          tdp_responsable_id: this.responsableSeleccionado
        })
        this.$vs.notify({ title: 'Éxito', text: 'Responsable asignado correctamente', color: 'success' })
        this.modalResponsable = false
        this.cargarDepartamentos()
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo asignar el responsable', color: 'danger' })
      } finally {
        this.guardandoResponsable = false
      }
    },

    // ─── Control de Jefes de Área (Tab 2 Root) ───────────────
    async cargarEmpleadosJefes() {
      if (this.empleadosJefes.length) return // ya cargado
      this.cargandoJefes = true
      try {
        const res = await this.$http.get(this.$server_url + 'actividades-control/empleados/activos')
        this.empleadosJefes = res.data || []
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo cargar la lista', color: 'danger' })
      } finally {
        this.cargandoJefes = false
      }
    },
    async cambiarJefatura(emp, valor) {
      this.guardandoJefe = emp.idusuario
      try {
        await this.$http.put(
          this.$server_url + `actividades-control/empleados/${emp.idusuario}/jefe`,
          { jefe_departamento: valor ? 1 : 0 }
        )
        // Actualizar localmente sin recargar todo
        emp.jefe_departamento = valor ? '1' : '0'
        this.$vs.notify({
          title: valor ? 'Jefatura otorgada' : 'Jefatura revocada',
          text: emp.nombre_completo,
          color: valor ? 'success' : 'warning',
          iconPack: 'feather',
          icon: valor ? 'icon-star' : 'icon-star-off'
        })
        // Refresca departamentos para actualizar responsables
        this.cargarDepartamentos()
        // Forzar reactive update
        this.empleadosJefes = [...this.empleadosJefes]
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo actualizar la jefatura', color: 'danger' })
      } finally {
        this.guardandoJefe = null
      }
    },

    // ─── Categorías ───────────────────────────────────────────
    async verCategorias(departamento) {
      this.departamentoSeleccionado = departamento
      this.mostrarFormCategoria = false
      this.modalCategorias = true
      await this.cargarCategoriasDepartamento(departamento.tdp_id)
    },
    async cargarCategoriasDepartamento(tdpId) {
      this.cargandoCategorias = true
      try {
        const res = await this.$http.get(this.$server_url + `actividades-control/categorias/${tdpId}`)
        this.categoriasDepartamento = res.data
      } catch (e) {
        console.error('Error al cargar categorías:', e)
        this.$vs.notify({ title: 'Error', text: 'No se pudieron cargar las categorías', color: 'danger' })
      } finally {
        this.cargandoCategorias = false
      }
    },
    abrirNuevaCategoria() {
      this.categoriaForm = { id_categoria: null, nombre_categoria: '', descripcion: '', color: '#3b82f6' }
      this.mostrarFormCategoria = true
    },
    editarCategoria(cat) {
      this.categoriaForm = {
        id_categoria: cat.id_categoria,
        nombre_categoria: cat.nombre_categoria,
        descripcion: cat.descripcion,
        color: cat.color
      }
      this.mostrarFormCategoria = true
    },
    async guardarCategoria() {
      if (!this.categoriaForm.nombre_categoria) {
        return this.$vs.notify({ title: 'Atención', text: 'El nombre es obligatorio', color: 'warning' })
      }
      if (this.guardandoCategoria) return
      this.guardandoCategoria = true
      try {
        const payload = {
          tdp_id: this.departamentoSeleccionado.tdp_id,
          nombre_categoria: this.categoriaForm.nombre_categoria,
          descripcion: this.categoriaForm.descripcion,
          color: this.categoriaForm.color
        }
        if (this.categoriaForm.id_categoria) {
          await this.$http.put(this.$server_url + `actividades-control/categorias/${this.categoriaForm.id_categoria}`, payload)
          this.$vs.notify({ title: 'Éxito', text: 'Categoría actualizada', color: 'success' })
        } else {
          await this.$http.post(this.$server_url + 'actividades-control/categorias', payload)
          this.$vs.notify({ title: 'Éxito', text: 'Categoría creada', color: 'success' })
        }
        this.mostrarFormCategoria = false
        this.cargarCategoriasDepartamento(this.departamentoSeleccionado.tdp_id)
        this.cargarDepartamentos()
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo guardar la categoría', color: 'danger' })
      } finally {
        this.guardandoCategoria = false
      }
    },
    cancelarFormCategoria() {
      this.mostrarFormCategoria = false
      this.categoriaForm = { id_categoria: null, nombre_categoria: '', descripcion: '', color: '#3b82f6' }
    },
    eliminarCategoria(idCategoria) {
      this.$vs.dialog({
        type: 'confirm', color: 'danger',
        title: 'Confirmar eliminación',
        text: '¿Está seguro de eliminar esta categoría? Se desactivará y dejará de estar disponible para nuevas tareas.',
        acceptText: 'Eliminar',
        cancelText: 'Cancelar',
        accept: () => this.confirmarEliminarCategoria(idCategoria)
      })
    },
    async confirmarEliminarCategoria(idCategoria) {
      this.guardandoCategoria = true
      try {
        await this.$http.delete(this.$server_url + `actividades-control/categorias/${idCategoria}`)
        this.categoriasDepartamento = this.categoriasDepartamento.filter(c => c.id_categoria !== idCategoria)
        this.$vs.notify({ title: 'Éxito', text: 'Categoría eliminada correctamente', color: 'success' })
        this.cargarDepartamentos()
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'No se pudo eliminar la categoría', color: 'danger' })
      } finally {
        this.guardandoCategoria = false
      }
    },
    iniciales(nombre) {
      if (!nombre) return '?'
      const parts = nombre.trim().split(' ')
      return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase()
    },
    getColorDepartamento(tdpId) {
      const colores = ['primary', 'success', 'warning', 'danger', 'dark', 'purple', 'pink']
      return colores[tdpId % colores.length]
    }
  }
}
</script>

<style scoped>
.configuracion-departamentos-container { max-width: 1400px; margin: 0 auto; }

/* Cards de control de jefes */
.jefe-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  transition: all 0.2s;
}
.jefe-card--activo {
  background: #f0fdf4;
  border-color: #86efac;
}
.jefe-avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; flex-shrink: 0;
}
.jefe-avatar--activo { background: #dcfce7; color: #16a34a; }
.jefe-info { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.jefe-nombre { font-size: 13px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.jefe-meta { font-size: 11px; color: #94a3b8; }
.jefe-actions { flex-shrink: 0; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Botón recargar giratorio */
.spin-icon .vs-button-icon i,
.spin-icon i.material-icons {
  animation: spin 0.8s linear infinite;
  display: inline-block;
}
</style>
